import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera, BookOpen, Share2, Languages, Cloud, ShieldCheck,
  School, Stethoscope, Store, Building2, Warehouse, Landmark,
  ArrowRight, Play, Check, Star, Quote, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Section, Eyebrow, H1, H2, Lead, FadeIn } from "../components/Primitives";
import { FEATURES, STEPS, INDUSTRIES, TESTIMONIALS, FAQS, PRICING, STATS } from "../data/content";

const iconMap = {
  Camera, BookOpen, Share2, Languages, Cloud, ShieldCheck,
  School, Stethoscope, Store, Building2, Warehouse, Landmark,
};

export default function HomePage() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="absolute inset-x-0 top-0 h-[480px] bg-gradient-to-b from-zinc-50 to-transparent" />
        <div className="container relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-24">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FadeIn>
                <Eyebrow testId="hero-eyebrow">No Pen · No Paper · Just Digital</Eyebrow>
              </FadeIn>
              <FadeIn delay={0.05}>
                <H1 className="mt-6">
                  Convert your manual<br />
                  registers to digital <span className="italic font-normal text-zinc-500">in seconds.</span>
                </H1>
              </FadeIn>
              <FadeIn delay={0.1}>
                <Lead className="mt-6">
                  Simply photograph your register — our OCR engine digitizes every row, column and signature.
                  Export to PDF, Excel, Sheets or WhatsApp. Built for Indian businesses.
                </Lead>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to="/try" data-testid="hero-cta-primary">
                    <Button size="lg" className="rounded-full bg-zinc-950 px-6 text-white hover:bg-zinc-800">
                      Try it now — free <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/try" data-testid="hero-cta-secondary">
                    <Button size="lg" variant="outline" className="rounded-full border-zinc-300 px-6 text-zinc-900 hover:bg-zinc-50">
                      <Play className="mr-1.5 h-4 w-4" /> Watch demo
                    </Button>
                  </Link>
                </div>
              </FadeIn>
              <FadeIn delay={0.2}>
                <div className="mt-10 flex items-center gap-6">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="h-8 w-8 rounded-full border-2 border-white bg-zinc-200 grid place-items-center text-xs font-semibold text-zinc-700">
                        {String.fromCharCode(64+i)}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-zinc-950 text-zinc-950" />
                      ))}
                    </div>
                    <span className="text-zinc-600"><span className="font-semibold text-zinc-950">500+</span> Indian businesses · 99% accuracy</span>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Hero visual */}
            <div className="lg:col-span-5">
              <FadeIn delay={0.15}>
                <HeroVisual />
              </FadeIn>
            </div>
          </div>

          {/* Logos / marquee */}
          <div className="mt-20 border-t border-zinc-200 pt-10">
            <p className="text-center text-xs uppercase tracking-widest text-zinc-500">Trusted across India</p>
            <div className="mt-6 overflow-hidden">
              <div className="marquee-track flex items-center gap-12 whitespace-nowrap text-zinc-400">
                {Array.from({ length: 2 }).map((_, dup) => (
                  <div key={dup} className="flex items-center gap-12">
                    {["Bharat Schools","City Hospital","Verma Traders","Patel Logistics","Gov. of UP","Sharma Clinic","DAV Public","Kisan Mandi"].map((n) => (
                      <span key={`${dup}-${n}`} className="font-poppins text-xl font-semibold tracking-tight">{n}</span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APP CTA BAND — register OCR */}
      <Section testId="home-apps" className="bg-zinc-950 text-white">
        <Link to="/try" data-testid="home-app-try" className="group relative block overflow-hidden rounded-3xl border border-white/10 bg-zinc-900 p-8 transition-all hover:-translate-y-0.5 hover:border-white/30">
          <Camera className="h-6 w-6 text-white" />
          <h3 className="mt-5 font-poppins text-2xl font-semibold">Register OCR</h3>
          <p className="mt-2 max-w-2xl text-sm text-zinc-300">Photo of any register → structured digital table. Export to PDF, Excel, Sheets, WhatsApp.</p>
          <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium">Open <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      </Section>

      {/* FEATURES */}
      <Section testId="home-features">
        <div className="grid items-end gap-6 md:grid-cols-2">
          <div>
            <Eyebrow>What you get</Eyebrow>
            <H2 className="mt-4">A complete digital register, in your pocket.</H2>
          </div>
          <Lead>Everything you need to retire that dusty old paper register — without learning a single new spreadsheet.</Lead>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = iconMap[f.icon];
            return (
              <FadeIn key={f.title} delay={i * 0.05}>
                <div
                  data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group h-full rounded-2xl border border-zinc-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-zinc-300 hover:shadow-md"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-950 text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-poppins text-lg font-semibold text-zinc-950">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600">{f.desc}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how-it-works" testId="home-how" className="bg-zinc-50 border-y border-zinc-200">
        <div className="grid gap-6 md:grid-cols-2 md:items-end">
          <div>
            <Eyebrow>How it works</Eyebrow>
            <H2 className="mt-4">From paper to pixels in three steps.</H2>
          </div>
          <Lead>No training, no setup, no IT team needed. If you can take a photo, you can use Register to Digital.</Lead>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <FadeIn key={s.n} delay={i * 0.08}>
              <div className="relative rounded-2xl border border-zinc-200 bg-white p-8 h-full">
                <div className="font-poppins text-5xl font-bold tracking-tighter text-zinc-200">{s.n}</div>
                <h3 className="mt-3 font-poppins text-xl font-semibold text-zinc-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-zinc-300 md:block" />
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* INDUSTRIES */}
      <Section testId="home-industries">
        <div className="text-center">
          <Eyebrow>Who is it for</Eyebrow>
          <H2 className="mx-auto mt-4 max-w-3xl">Built for every Indian workplace that still touches paper.</H2>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {INDUSTRIES.map((ind) => {
            const Icon = iconMap[ind.icon];
            return (
              <div
                key={ind.label}
                data-testid={`industry-${ind.label.toLowerCase()}`}
                className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-zinc-200 bg-white p-6 text-center transition-all hover:-translate-y-0.5 hover:border-zinc-950"
              >
                <Icon className="h-6 w-6 text-zinc-900" />
                <span className="text-sm font-medium text-zinc-800">{ind.label}</span>
              </div>
            );
          })}
        </div>
      </Section>

      {/* STATS BAND */}
      <section className="bg-zinc-950 text-white">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-poppins text-4xl sm:text-5xl font-bold tracking-tight">{s.value}</div>
                <div className="mt-2 text-xs uppercase tracking-wider text-zinc-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Section testId="home-testimonials">
        <div className="grid gap-6 md:grid-cols-2 md:items-end">
          <div>
            <Eyebrow>Loved by Indian teams</Eyebrow>
            <H2 className="mt-4">Don&apos;t take our word for it.</H2>
          </div>
          <Lead>From Meerut to Madurai, businesses are quietly retiring their paper registers.</Lead>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <FadeIn key={t.name} delay={i * 0.07}>
              <div className="h-full rounded-2xl border border-zinc-200 bg-white p-7">
                <Quote className="h-6 w-6 text-zinc-300" />
                <p className="mt-4 text-base leading-relaxed text-zinc-800">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-6 flex items-center gap-3 border-t border-zinc-100 pt-5">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-zinc-950 font-poppins text-sm font-semibold text-white">
                    {t.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-zinc-950">{t.name}</div>
                    <div className="text-xs text-zinc-500">{t.role}</div>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* FREE FOREVER BAND */}
      <Section testId="home-pricing" className="bg-zinc-50 border-y border-zinc-200">
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>Pricing</Eyebrow>
          <H2 className="mt-4">Free forever. Every feature, every photo.</H2>
          <Lead className="mx-auto mt-5">
            No subscription. No payment wall. No upsells. We&apos;ll never charge you for digitizing your registers.
          </Lead>
          <Link to="/try" data-testid="home-pricing-cta">
            <Button size="lg" className="mt-7 rounded-full bg-zinc-950 px-6 text-white hover:bg-zinc-800">
              Try it now — free <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* FAQ */}
      <Section testId="home-faq">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <Eyebrow>Frequently asked</Eyebrow>
            <H2 className="mt-4">Questions we hear, answered.</H2>
            <Lead className="mt-4">Still curious? Reach out — we typically respond on the same business day.</Lead>
            <Link to="/contact" data-testid="faq-contact-cta">
              <Button className="mt-6 rounded-full bg-zinc-950 text-white hover:bg-zinc-800">Talk to us</Button>
            </Link>
          </div>
          <div className="md:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-zinc-200">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-poppins text-base font-semibold text-zinc-950 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-zinc-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section testId="home-cta-final" className="pb-32">
        <div className="relative overflow-hidden rounded-3xl bg-zinc-950 p-10 md:p-16 text-center">
          <div className="absolute inset-0 bg-dotted opacity-10" />
          <div className="relative">
            <Eyebrow className="border-white/20 bg-white/5 text-white"><span className="h-1.5 w-1.5 rounded-full bg-white" /> Ready when you are</Eyebrow>
            <h2 className="mt-5 font-poppins text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Stop writing. Start digitizing.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm sm:text-base text-zinc-300">
              Try Register to Digital free for 14 days. No credit card. No commitments. Just clean digital registers.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/try" data-testid="final-cta-primary">
                <Button size="lg" className="rounded-full bg-white px-6 text-zinc-950 hover:bg-zinc-100">
                  Try it now — free <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="final-cta-secondary">
                <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white/10 hover:text-white">
                  See pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-2xl"
        data-testid="hero-visual"
      >
        <img
          src="/hero-scan.jpg"
          alt="Scanning a handwritten Indian register with a smartphone — digitizing rows into a clean table."
          loading="eager"
          decoding="async"
          fetchPriority="high"
          width={1600}
          height={1000}
          className="block h-auto w-full select-none"
          draggable={false}
        />
        {/* subtle inner ring for premium feel */}
        <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-zinc-900/5" />
      </motion.div>

      {/* Floating "scanning" badge — visible on md+ */}
      <motion.div
        initial={{ opacity: 0, y: 20, rotate: -6 }}
        animate={{ opacity: 1, y: 0, rotate: -6 }}
        transition={{ delay: 0.35, duration: 0.7 }}
        className="absolute -left-5 -top-5 hidden items-center gap-2 rounded-2xl border border-zinc-200 bg-white/90 px-3 py-2 shadow-xl backdrop-blur md:flex"
        data-testid="hero-badge-scanning"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
        <span className="text-[11px] font-medium uppercase tracking-widest text-zinc-700">
          Scanning…
        </span>
      </motion.div>

      {/* Floating "Digitized" stat — visible on md+ */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.7 }}
        className="absolute -bottom-6 right-4 hidden rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-xl md:block"
        data-testid="hero-badge-digitized"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-zinc-950 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Digitized</div>
            <div className="font-poppins text-sm font-semibold text-zinc-950">in 3.2 sec</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
